import { useId, type ReactNode } from "react";

import { Button, type ButtonSize, type ButtonVariant } from "./Button.js";
import { classes } from "./classes.js";

/**
 * `P0-UIK-002`: invariant **I19**, made impossible to break.
 *
 * > **A guarded action always displays the reason it is blocked.**
 *
 * **Danger sentence.** A greyed-out button with no explanation is the single
 * most expensive pixel in a workflow product: the user does not know whether
 * they lack a right, whether a document is missing, or whether the software is
 * broken , so they telephone somebody, and somebody at Hakili spends four
 * minutes reading a screen back to them. Multiply by every guarded action on
 * every screen. I19 exists because that call is the product failing quietly.
 *
 * **The type is the enforcement, not the render.** `GuardedProps` is a
 * discriminated union: `{ disabled: true }` requires `reason`, and
 * `{ disabled?: false }` forbids it. A caller cannot construct a disabled
 * button without a reason , not "should not", cannot , and the fixture in
 * `test/__fixtures__/guarded-button/` proves the impossible construction fails
 * to compile, with a control beside it that must compile.
 *
 * **`DX-009` (D-1018-6):** the reason is VISIBLE text beside the control, and the
 * control stays focusable (`aria-disabled`).
 *
 * The runtime check stays anyway, for the caller who arrives through untyped
 * data: an empty string is a reason nobody wrote, and it is refused loudly
 * rather than rendered as a blank space beside a dead control.
 */

interface GuardedBase {
  readonly children: ReactNode;
  readonly variant?: ButtonVariant;
  /**
   * `SIGNIN-VEILLEUR` (D2 `screens2.js:170`, `components.css:84`): the size, as `Button` has it. **Opt-in: the default is the kit's 40 px**, and only a caller that
   * draws D2's `lg` (the sign-in's « Se connecter », 48 px) asks for it; the inert branch carries the same `data-size` the bank's skin reads.
   */
  readonly size?: ButtonSize;
  readonly className?: string;
  /** `round CN-B`: a hook for a spec that pins WHICH control is greyed and why. */
  readonly testId?: string;
  /**
   * `DX-2`: `"submit"` makes the live control its form's submit button. Greyed, it
   * is STILL rendered as a submit button , so that Enter typed in a field of its
   * form clicks THIS button (the form's default button) and is swallowed here,
   * instead of finding no default button and submitting through the form's
   * single field.
   */
  readonly type?: "button" | "submit";
  /**
   * `D-1019-3`: the colour of the reason. A reason that WARNS (something is missing, something blocks) is amber, the default; a reason
   * that states a FACT (« aucune banque n'est cochée », « c'est fait ») is `"neutral"`, never amber.
   */
  readonly reasonTone?: "warning" | "neutral";
  /**
   * `COMITE-RECORD-3` (D2 frame 111): the reason drawn as D2's lock chip (`.guard`: an amber fill, a rounded pill carrying the
   * lock) instead of plain text beside the control. Default `"text"`; the sentence is the same, only its dress changes.
   */
  readonly reasonDress?: "text" | "chip";
  /** The lock the chip carries (a `ReactNode`, as the kit's other icons are). Drawn only with `reasonDress="chip"`. */
  readonly reasonIcon?: React.ReactNode;
}

export type GuardedButtonProps = GuardedBase &
  (
    | {
        readonly disabled: true;
        /** The French sentence shown beside the control. I19 requires it. */
        readonly reason: string;
        readonly onClick?: never;
      }
    | {
        readonly disabled?: false;
        readonly reason?: never;
        readonly onClick?: () => void;
      }
  );

/** A greyed control takes the click and does nothing with it, and lets nobody else have it. */
const swallow = (event: React.SyntheticEvent): void => {
  event.preventDefault();
  event.stopPropagation();
};

/** Enter (on keydown) and Space (on keyup) are what activate a focused button; Tab and the arrows must still move on. */
const swallowKey = (event: React.KeyboardEvent): void => {
  if (event.key === "Enter" || event.key === " ") swallow(event);
};

export function GuardedButton(props: GuardedButtonProps): React.JSX.Element {
  const {
    children,
    variant,
    size,
    className,
    type = "button",
    reasonTone = "warning",
    reasonDress = "text",
    reasonIcon,
  } = props;
  const reasonId = useId();

  if (props.disabled === true) {
    if (props.reason.trim() === "") {
      // Reachable only from untyped data, and worth refusing there too: a
      // blank reason renders as a dead control beside empty space, which is
      // the exact experience I19 forbids.
      throw new Error("GuardedButton: a disabled action needs a non-empty reason (I19)");
    }
    return (
      <span className={classes("inline-flex flex-wrap items-center gap-x-3 gap-y-1", className)}>
        <button
          // `DX-009` (D-1018-6): **`aria-disabled`, never the `disabled` attribute.**
          // A `disabled` button leaves the tab order and cannot be focused, so a
          // keyboard or screen-reader user never lands on it and never hears why
          // it is dead. `aria-disabled` keeps it focusable and announced as
          // unavailable; there is no `onClick` on this branch (the type forbids
          // one), so it does nothing when pressed.
          aria-disabled="true"
          aria-describedby={reasonId}
          type={type}
          // **`aria-disabled` blocks nothing by itself** (architect's note, Lane F):
          // the browser still activates the button on a click, on Enter and on
          // Space, still submits its form, and still bubbles the click to a
          // clickable parent. So the refusal is ours: the click is cancelled and
          // stopped (no submit, no parent), and the two keys that activate a
          // button are cancelled before they can. `guarded-inert.spec.tsx` proves it.
          onClick={swallow}
          onKeyDown={swallowKey}
          onKeyUp={swallowKey}
          data-testid={props.testId}
          data-kit="guarded"
          data-size={size}
          // `R68`'s rule, and the same reason: « Modifier le / prix » on two
          // lines names no gesture.
          className="cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium whitespace-nowrap text-gray-400"
        >
          {children}
        </button>
        {/**
         * `DX-009`, D-1018-6 (18 October): **the reason is on the screen, beside
         * the control, in amber , « on ne cache jamais pourquoi on ne peut pas
         * avancer » (`hakili-console.jsx`).**
         *
         * This REVERSES the narrowing of 7 September (`round F3`, `W5`, `R81`),
         * which had moved the reason into the `title` so a fiche would not carry
         * a sentence of prose per greyed button. Both texts are kept: that
         * ruling is why the sentences are short and why an unbuilt thing says
         * « Bientôt disponible »; this one is why a person who cannot hover , a
         * phone, a keyboard, a screen reader , still reads it. `I19` as written
         * stands again: *"a non-empty reason string is rendered next to it."*
         */}
        <span
          id={reasonId}
          data-kit="guarded-reason"
          data-testid={props.testId === undefined ? undefined : `${props.testId}-raison`}
          // A reason is a sentence a person reads , reading text, 14 px (D-1018-6, 18 October 2026: « le
          // texte de lecture passe à 14 px »). Set at 12 px by DX-2 and measured red by the width contract.
          data-dress={reasonDress}
          // `GUARDED-CHIP-GROUND`: D2's `.guard` ground is `#FFEFCF` (`--hk-warn-bg`), as `ARC-1` drew it; `bg-amber-50` was paler.
          className={classes(
            "text-sm leading-snug",
            reasonTone === "neutral" ? "text-gray-700" : "text-amber-700",
            reasonDress === "chip" && "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5",
            reasonDress === "chip" &&
              (reasonTone === "neutral" ? "bg-gray-100" : "bg-[color:var(--hk-warn-bg)]"),
          )}
        >
          {reasonDress === "chip" ? reasonIcon : null}
          {props.reason}
        </span>
      </span>
    );
  }

  // Spread conditionally rather than passing `undefined`: the repository runs
  // `exactOptionalPropertyTypes`, so an omitted prop and a prop explicitly set
  // to `undefined` are different types , and the compiler said so.
  const forwarded = {
    ...(variant === undefined ? {} : { variant }),
    ...(size === undefined ? {} : { size }),
    ...(type === "submit" ? { type } : {}),
    ...(props.onClick === undefined ? {} : { onClick: props.onClick }),
    ...(className === undefined ? {} : { className }),
    /**
     * `FX-2a`, `M1`: **the id survives the control coming ALIVE.**
     *
     * It was written only on the disabled branch, and the doc above said why ,
     * « a hook for a spec that pins WHICH control is greyed and why ». That is
     * half of what a spec has to prove. `F1`'s corridor walks the dealer from a
     * greyed « Mettre en ligne » through Profil to the same control LIVE, and
     * with the id dropped on this branch it could not find the control it had
     * just been holding: it would have read `null` and been told the button was
     * absent when it was merely anonymous , `R68`'s own defect, one component
     * along.
     */
    ...(props.testId === undefined ? {} : { "data-testid": props.testId }),
  };
  return <Button {...forwarded}>{children}</Button>;
}
